import {
  postWithAuth,
  getWithAuth,
  fetchWithAuth,
  postStreamWithAuth,
} from '@/lib/httpAdapter';
import apiUrl from './api';
import { normalizeUrl } from '@/lib/utils/url';
import type {
  SendMessageRequest,
  SendMessageResponse,
  GetConversationsRequest,
  GetConversationsResponse,
  GetConversationMessagesRequest,
  GetConversationMessagesResponse,
  DeleteConversationRequest,
  DeleteConversationResponse,
  GetSuggestedQuestionsRequest,
  GetSuggestedQuestionsResponse,
} from '@/types/assistantChat';

// Normalize URL construction to avoid double slashes
// apiUrl already respects NEXT_PUBLIC_ENVIRONMENT (dev → local, prod → production)
const AI_CHAT_BASE_URL = normalizeUrl(apiUrl, 'api/ai-chat');

// ============================================================================
// Send Message
// ============================================================================

export const sendMessage = async (
  request: SendMessageRequest
): Promise<SendMessageResponse> => {
  if (!request.userUUID || !request.orgUUID || !request.prompt) {
    throw new Error(
      'Missing required parameters: userUUID, orgUUID, and prompt'
    );
  }

  const url = request.stream
    ? `${AI_CHAT_BASE_URL}/chat/?stream=true`
    : `${AI_CHAT_BASE_URL}/chat/`;

  const res = await postWithAuth(url, request);

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to send message: ${res.statusText} - ${errorText}`);
  }

  const contentType = res.headers.get('content-type') || '';
  if (request.stream && !contentType.includes('application/json')) {
    return {
      message: '',
      conversationId: '',
      threadId: '',
    };
  }

  const data = await res.json();

  // Handle both string and object responses
  if (typeof data === 'string') {
    return JSON.parse(data);
  }

  return data;
};

// ============================================================================
// Stream Chat (returns raw Response for SSE streaming)
// ============================================================================

export interface StreamChatRequest {
  userUUID: string;
  orgUUID: string;
  prompt: string | { id?: string; role?: string; content?: string };
  threadId?: string;
  responseId?: string;
  conversationUUID?: string;
  title?: string;
  userName?: string;
  githubUsername?: string;
  gitlabUsername?: string;
  bitbucketUsername?: string;
  timezone?: string;
}

export const streamChat = async (
  request: StreamChatRequest,
  signal?: AbortSignal
): Promise<Response> => {
  if (!request.userUUID || !request.orgUUID || !request.prompt) {
    throw new Error(
      'Missing required parameters: userUUID, orgUUID, and prompt'
    );
  }

  // Goes direct to the backend (not via the BFF) with a scoped stream token:
  // chat_view now requires auth, and proxying a multi-minute SSE response
  // through a Vercel function would cap it at 180s.
  const res = await postStreamWithAuth(
    `${AI_CHAT_BASE_URL}/chat/?stream=true`,
    request,
    { signal }
  );

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`HTTP ${res.status}: ${errorText}`);
  }

  return res;
};

// ============================================================================
// Get Conversations
// ============================================================================

export const getConversations = async (
  request: GetConversationsRequest
): Promise<GetConversationsResponse> => {
  if (!request.userUUID || !request.orgUUID) {
    throw new Error('Missing required parameters: userUUID and orgUUID');
  }

  const params = new URLSearchParams({
    userUUID: request.userUUID,
    orgUUID: request.orgUUID,
    ...(request.limit != null && { limit: request.limit.toString() }),
    ...(request.offset != null && { offset: request.offset.toString() }),
  });

  const res = await getWithAuth(
    `${AI_CHAT_BASE_URL}/conversations/?${params.toString()}`
  );

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(
      `Failed to get conversations: ${res.statusText} - ${errorText}`
    );
  }

  const data = await res.json();

  // Handle both string and object responses
  if (typeof data === 'string') {
    return JSON.parse(data);
  }

  return data;
};

// ============================================================================
// Get Conversation Messages
// ============================================================================

export const getConversationMessages = async (
  request: GetConversationMessagesRequest,
  signal?: AbortSignal
): Promise<GetConversationMessagesResponse> => {
  try {
    if (!request.conversationUUID || !request.userUUID || !request.orgUUID) {
      throw new Error(
        'Missing required parameters: conversationUUID, userUUID, and orgUUID'
      );
    }

    const params = new URLSearchParams({
      userUUID: request.userUUID,
      orgUUID: request.orgUUID,
      ...(request.limit != null && { limit: request.limit.toString() }),
      ...(request.offset != null && { offset: request.offset.toString() }),
      ...(request.order && { order: request.order }),
    });

    const res = await getWithAuth(
      `${AI_CHAT_BASE_URL}/conversations/${request.conversationUUID}/messages/?${params.toString()}`,
      { signal }
    );

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(
        `Failed to get conversation messages: ${res.statusText} - ${errorText}`
      );
    }

    const data = await res.json();

    // Handle both string and object responses
    if (typeof data === 'string') {
      return JSON.parse(data);
    }

    return data;
  } catch (err: unknown) {
    // Don't log error if request was aborted (expected behavior)
    if (err instanceof Error && err.name === 'AbortError') {
      throw err;
    }

    console.error('Error getting conversation messages:', err);
    throw err;
  }
};

// ============================================================================
// Delete Conversation
// ============================================================================

export const deleteConversation = async (
  request: DeleteConversationRequest
): Promise<DeleteConversationResponse> => {
  if (!request.conversationUUID || !request.userUUID || !request.orgUUID) {
    throw new Error(
      'Missing required parameters: conversationUUID, userUUID, and orgUUID'
    );
  }

  const params = new URLSearchParams({
    userUUID: request.userUUID,
    orgUUID: request.orgUUID,
  });

  const res = await fetchWithAuth(
    `${AI_CHAT_BASE_URL}/conversations/${request.conversationUUID}/?${params.toString()}`,
    {
      method: 'DELETE',
    }
  );

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(
      `Failed to delete conversation: ${res.statusText} - ${errorText}`
    );
  }

  const data = await res.json();

  // Handle both string and object responses
  if (typeof data === 'string') {
    return JSON.parse(data);
  }

  return data;
};

// ============================================================================
// Get Suggested Questions
// ============================================================================

export const getSuggestedQuestions = async (
  request: GetSuggestedQuestionsRequest
): Promise<GetSuggestedQuestionsResponse> => {
  if (!request.userUUID || !request.orgUUID) {
    throw new Error('Missing required parameters: userUUID and orgUUID');
  }

  const params = new URLSearchParams({
    userUUID: request.userUUID,
    orgUUID: request.orgUUID,
  });

  const res = await getWithAuth(
    `${AI_CHAT_BASE_URL}/suggested-questions/?${params.toString()}`
  );

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(
      `Failed to get suggested questions: ${res.statusText} - ${errorText}`
    );
  }

  const data = await res.json();

  // Handle both string and object responses
  if (typeof data === 'string') {
    return JSON.parse(data);
  }

  return data;
};
